import { aiPlayers, type HostGameSummary } from "@kalaporssi/shared";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Board } from "../components/Board";
import { ErrorBox, errorMessage, Loading } from "../components/Misc";
import { api } from "../lib/api";
import { completeLogin, isSignedIn, logout, signedInEmail, startLogin } from "../lib/auth";
import { monthName } from "../lib/format";

const STATUS_FI: Record<HostGameSummary["status"], string> = {
  LOBBY: "Aula",
  STARTED: "Alkamassa",
  ROUND_OPEN: "Kierros auki",
  ROUND_RESOLVED: "Kierros ratkaistu",
  FINISHED: "Päättynyt",
};

/** Redirects to Cognito managed login when signed out. */
export function RequireHost({ children }: { children: ReactNode }) {
  const loc = useLocation();
  const signedIn = isSignedIn();
  useEffect(() => {
    if (!signedIn) void startLogin(loc.pathname);
  }, [signedIn, loc.pathname]);
  if (!signedIn) return <Loading text="Siirrytään kirjautumiseen…" />;
  return <>{children}</>;
}

export function HostCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    completeLogin(loc.search)
      .then((to) => nav(to, { replace: true }))
      .catch(setError);
  }, [loc.search, nav]);
  return (
    <>
      <Board />
      <main>{error ? <ErrorBox error={error} /> : <Loading text="Kirjaudutaan…" />}</main>
    </>
  );
}

export function HostHome() {
  const nav = useNavigate();
  const games = useQuery({ queryKey: ["hostGames"], queryFn: () => api().listHostGames() });
  const [ais, setAis] = useState<string[]>(aiPlayers.map((a) => a.id));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const { code } = await api().createGame(ais);
      nav(`/host/${code}`);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  const toggle = (id: string) => setAis((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  return (
    <>
      <Board />
      <main className="stack">
        <div className="row spread">
          <h2>Pelinjohtaja</h2>
          <span className="muted small">
            {signedInEmail()}{" "}
            <button type="button" className="btn ghost small" onClick={logout}>
              Kirjaudu ulos
            </button>
          </span>
        </div>
        <div className="panel stack">
          <h3>Uusi peli</h3>
          {aiPlayers.map((a) => (
            <label className="ck" key={a.id}>
              <input type="checkbox" checked={ais.includes(a.id)} onChange={() => toggle(a.id)} />
              <span>
                <b>{a.name}</b>
                <br />
                <span className="muted">
                  {a.id === "ai_s" ? "Clauden tasapainoinen malli." : "Clauden järein malli, pohtii pidempään."}{" "}
                  Perustelut paljastetaan pelin lopussa.
                </span>
              </span>
            </label>
          ))}
          <div>
            <button type="button" className="btn tag" onClick={create} disabled={busy}>
              {busy ? "Luodaan…" : "Luo peli"}
            </button>
          </div>
          <ErrorBox error={error} />
        </div>
        <div className="panel stack">
          <h3>Omat pelit</h3>
          {games.isLoading && <Loading />}
          {games.error && <ErrorBox error={games.error} />}
          {games.data?.games.length === 0 && <p className="muted">Ei vielä pelejä.</p>}
          {games.data && games.data.games.length > 0 && (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Koodi</th>
                    <th>Tila</th>
                    <th>Kuukausi</th>
                    <th>Pelaajia</th>
                    <th>Luotu</th>
                  </tr>
                </thead>
                <tbody>
                  {games.data.games.map((g) => (
                    <tr key={g.code}>
                      <td>
                        <Link to={`/host/${g.code}`}>
                          <b>{g.code}</b>
                        </Link>
                      </td>
                      <td>{STATUS_FI[g.status]}</td>
                      <td>{g.currentRound > 0 ? monthName(g.currentRound - 1) : "–"}</td>
                      <td className="num">{g.playerCount}</td>
                      <td className="num">{new Date(g.createdAt).toLocaleString("fi-FI")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

export { errorMessage };
